import React, { useState, useEffect } from 'react';
import { Mic, Check, Loader2 } from 'lucide-react';

const VoiceAnimationDemo = () => {
  const [step, setStep] = useState(0);

  const demoSteps = [
    {
      phase: 'idle',
      display: 'Click microphone to speak',
      showMic: true,
      micActive: false,
      showWaveform: false,
      showTranscript: false,
      showResult: false
    },
    {
      phase: 'listening',
      display: 'Listening...',
      showMic: true,
      micActive: true,
      showWaveform: true,
      showTranscript: false,
      showResult: false
    },
    {
      phase: 'transcribing',
      display: 'Processing...',
      showMic: false,
      micActive: false,
      showWaveform: false,
      showTranscript: true,
      transcript: '"Submit PTO for next Monday"',
      showResult: false
    },
    {
      phase: 'processing',
      display: 'Executing...',
      showMic: false,
      micActive: false,
      showWaveform: false,
      showTranscript: true,
      transcript: '"Submit PTO for next Monday"',
      showResult: false,
      processing: true
    },
    {
      phase: 'complete',
      display: 'Task completed',
      showMic: false,
      micActive: false,
      showWaveform: false,
      showTranscript: true,
      transcript: '"Submit PTO for next Monday"',
      showResult: true,
      result: {
        title: 'PTO Request Submitted',
        detail: 'Leave request for Monday created successfully',
        time: '8 seconds'
      }
    }
  ];

  useEffect(() => {
    const intervals = [2000, 2000, 1500, 1500, 3000]; // Time for each step
    const timer = setTimeout(() => {
      setStep((prev) => (prev + 1) % demoSteps.length);
    }, intervals[step]);

    return () => clearTimeout(timer);
  }, [step]);

  const currentStep = demoSteps[step];

  return (
    <div className="relative bg-gradient-to-br from-accent-purple-200 to-accent-blue-200 rounded-xl p-8 min-h-[400px] flex flex-col items-center justify-center">
      {/* Status Text */}
      <div className="absolute top-6 left-6">
        <span className="caption bg-white/60 backdrop-blur-sm px-3 py-1.5 rounded-full">
          {currentStep.display}
        </span>
      </div>

      {/* Main Animation Area */}
      <div className="flex flex-col items-center justify-center space-y-6 w-full">
        {/* Microphone Button */}
        {currentStep.showMic && (
          <button
            className={`relative w-20 h-20 rounded-full transition-all duration-500 flex items-center justify-center ${
              currentStep.micActive
                ? 'bg-red-500 scale-110 shadow-lg shadow-red-500/50 animate-pulse'
                : 'bg-white hover:bg-gray-50 shadow-md'
            }`}
          >
            <Mic className={`w-10 h-10 ${currentStep.micActive ? 'text-white' : 'text-gray-700'}`} />
            
            {/* Ripple effect when active */}
            {currentStep.micActive && (
              <>
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-30"></span>
                <span className="absolute inset-0 rounded-full bg-red-500 animate-ping opacity-20" style={{ animationDelay: '0.5s' }}></span>
              </>
            )}
          </button>
        )}

        {/* Waveform Animation */}
        {currentStep.showWaveform && (
          <div className="flex items-center justify-center gap-1.5 h-16">
            {[...Array(5)].map((_, i) => (
              <div
                key={i}
                className="w-1.5 bg-red-500 rounded-full animate-waveform"
                style={{
                  animationDelay: `${i * 0.1}s`,
                  height: '20px'
                }}
              />
            ))}
          </div>
        )}

        {/* Transcript Display */}
        {currentStep.showTranscript && (
          <div className="bg-white rounded-lg px-6 py-4 shadow-md max-w-md w-full fade-in-up">
            <div className="flex items-start gap-3">
              <Mic className="w-5 h-5 mt-1 text-blue-600 flex-shrink-0" />
              <p className="body-medium text-gray-700 italic">{currentStep.transcript}</p>
            </div>
          </div>
        )}

        {/* Processing Indicator */}
        {currentStep.processing && (
          <div className="flex items-center gap-2 text-text-muted">
            <Loader2 className="w-5 h-5 animate-spin" />
            <span className="body-small">Checking policies & executing...</span>
          </div>
        )}

        {/* Result Display */}
        {currentStep.showResult && (
          <div className="bg-green-50 border-2 border-green-500 rounded-lg px-6 py-4 shadow-md max-w-md w-full fade-in-up">
            <div className="flex items-start gap-3">
              <div className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center flex-shrink-0">
                <Check className="w-6 h-6 text-white" />
              </div>
              <div className="flex-1">
                <h4 className="heading-3 text-sm mb-1 text-green-900">{currentStep.result.title}</h4>
                <p className="body-small text-green-800 mb-2">{currentStep.result.detail}</p>
                <span className="caption text-green-700 font-medium">⚡ Completed in {currentStep.result.time}</span>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Progress Dots */}
      <div className="absolute bottom-6 flex gap-2">
        {demoSteps.map((_, idx) => (
          <div
            key={idx}
            className={`w-2 h-2 rounded-full transition-all ${
              idx === step ? 'bg-text-primary w-6' : 'bg-white/50'
            }`}
          />
        ))}
      </div>

      {/* CSS Animation Styles */}
      <style jsx>{`
        @keyframes waveform {
          0%, 100% {
            height: 20px;
          }
          50% {
            height: 50px;
          }
        }
        .animate-waveform {
          animation: waveform 1s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
};

export default VoiceAnimationDemo;
